import React, { useState, useEffect, useRef } from 'react';
import server from '../../api/server';
import "../loader.css"
import { useToast } from '../../context/ToastContext.jsx';
import SampleTestModal from '../layouts/SampleTestModal.jsx'
import { IoMicOutline } from "react-icons/io5";
import { IoChatbubbleOutline } from "react-icons/io5";
import FeedbackSection from '../layouts/FeedbackSection.jsx';
import { Save, Upload, Download } from 'lucide-react';
import DeployConfirmationModal from '../layouts/DeployConfirmationModal.jsx';
import { getApiToastMessage } from '../../utils/apiToastMessage.js';
import ConfigurationDiffButton from '../layouts/ConfigurationDiffButton.jsx';

const SummaryConfigure = ({clientID, serviceID, module_name, setCloseModal, config_data}) => {
  const { showToast } = useToast();
  const [showPreview, setShowPreview] = useState(false);
  const [expandedSample, setExpandedSample] = useState(null)
  const [summaryModule, setSummaryModule] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showDeployConfirmation, setShowDeployConfirmation] = useState(false)
  const [sampleCalls, setSampleCalls] = useState([])
  const [selectedSample, setSelectedSample] = useState(0)
  const [moduleActive, setModuleActive] = useState(false)
  const [defaultConfig, setDefaultConfig] = useState(null)
  const [defaultActive, setDefaultActive] = useState(false)
  const [configType, setConfigType] = useState('Live Version')
  const [current_version, setCurrent_version] = useState(0)
  const [showConfigDropdown, setShowConfigDropdown] = useState(false)
  const [moduleSaveDraftConfiguration, setModuleSaveDraftConfiguration] = useState(null)
  const [showSampleTest, setShowSampleTest] = useState(false)
  const [showSampleTestModal, setShowSampleTestModal] = useState(false)
  const segmentRefs = useRef([]);
  const sourceClickInProgressRef = useRef(false);
  const audioRef = useRef(null);
  const [selectedSegmentIdx, setSelectedSegmentIdx] = useState(null);
  const [role] = useState(()=> {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const role = userData.role
      return role
    }
    return 'admin';
  })
  
  // Upload and Download Configuration Functions
  const handleDownloadConfig = () => {
    try {
      const configData = {
        summaryModule: summaryModule,
        moduleActive: moduleActive
      };

      const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation_summary_config_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Configuration downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading configuration:', error);
      showToast('Error downloading configuration', 'error');
    }
  };

  const handleUploadConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const configData = JSON.parse(e.target.result);
        setLoading(true);
        const validationConfig = {
          configuration: configData.summaryModule,
          active: configData.moduleActive === true ? 1 : 0
        };
        const validationResp = await server.validateModuleJsonConfiguration({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'conversation_summary', configuration: validationConfig});
        if (validationResp.status !== 200) {
          showToast(await getApiToastMessage(validationResp, 'Invalid configuration'), 'error');
          setLoading(false);
          event.target.value = '';
          return;
        }
        
        // Validate the uploaded configuration
        if (!configData.summaryModule) {
          throw new Error('Invalid configuration format');
        }

        // Update the state with uploaded configuration
        if (configData.summaryModule) {
          setSummaryModule(configData.summaryModule);
        }
        
        if (configData.moduleActive !== undefined) {
          setModuleActive(configData.moduleActive);
        }

        showToast(await getApiToastMessage(validationResp, 'Configuration uploaded successfully!'), 'success');
        setLoading(false);
      } catch (error) {
        console.error('Error parsing uploaded configuration:', error);
        showToast('Error uploading configuration: Invalid JSON format', 'error');
        setLoading(false);
      }
    };
    
    reader.readAsText(file);
  };

  const scrollToSegment = (idx) => {
    const el = segmentRefs.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };  
  const convertToSeconds = (time) => {
    if(typeof time === 'string'){
      const [hours, minutes, seconds] = time.split(':').map(Number);
      return (hours * 3600) + (minutes * 60) + seconds;
    }
    return time;
  }
  const handleSourceClick = (source, segments) => {
    let start, end, message_id;
    if(source.start_timestamp && source.end_timestamp){
      // Set flag to prevent automatic segment selection
      sourceClickInProgressRef.current = true;
      
      // Convert timestamps to seconds with proper validation
      try {
        if(typeof source.start_timestamp === 'string'){
          start = convertToSeconds(source.start_timestamp)-1;
        }else{
          start = parseFloat(source.start_timestamp)-1;
        }
        if(typeof source.end_timestamp === 'string'){
          end = convertToSeconds(source.end_timestamp)+1;
        }else{
          end = parseFloat(source.end_timestamp)+1;
        }
        
        // Validate that start and end are valid numbers
        if (isNaN(start) || isNaN(end)) {
          console.warn('Invalid timestamp values:', source.start_timestamp, source.end_timestamp);
          sourceClickInProgressRef.current = false;
          return;
        }
        
        // Ensure start time is always less than end time
        if (start >= end) {
          console.warn('Start time must be less than end time:', start, end);
          sourceClickInProgressRef.current = false;
          return;
        }
        
      } catch (error) {
        console.warn('Error converting timestamps:', error);
        sourceClickInProgressRef.current = false;
        return;
      }
      
      // Handle audio case
      // Check if audio element exists and is valid
      if (!audioRef.current) {
        console.warn('Audio element not available');
        sourceClickInProgressRef.current = false;
        return;
      }

      try {
        // Pause any currently playing audio first
        audioRef.current.pause();
        
        // Remove any existing timeupdate listeners
        if (audioRef.current._currentTimeUpdateHandler) {
          audioRef.current.removeEventListener('timeupdate', audioRef.current._currentTimeUpdateHandler);
          audioRef.current._currentTimeUpdateHandler = null;
        }
        
        // Create new timeupdate handler
        const timeUpdateHandler = () => {
          if (!audioRef.current) return;
          
          const currentTime = audioRef.current.currentTime;
          
          if (currentTime >= end) {
            audioRef.current.pause();
            audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
            audioRef.current._currentTimeUpdateHandler = null;
          }
        };
        
        // Store reference to handler for cleanup
        audioRef.current._currentTimeUpdateHandler = timeUpdateHandler;
        
        // Add the new listener
        audioRef.current.addEventListener('timeupdate', timeUpdateHandler);
        
        // Seek to start time
        audioRef.current.currentTime = start;
        
        // Play with proper error handling
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Find the segment index whose range includes the start time
              let idx = 0;
              for (let i = 0; i < segments.length; i++) {
                if (start >= segments[i].start_time && start <= segments[i].end_time) {
                  idx = i;
                  break;
                }
              }
              setSelectedSegmentIdx(idx);
              
              // Scroll the segment into view
              if (segmentRefs.current[idx] && idx === selectedSegmentIdx) {
                segmentRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "nearest" });
              }
              
              // Clear the flag after successful playback start
              sourceClickInProgressRef.current = false;
            })
            .catch(error => {
              console.warn('Error playing audio:', error);
              // Clean up listener on error
              audioRef.current.removeEventListener('timeupdate', timeUpdateHandler);
              audioRef.current._currentTimeUpdateHandler = null;
              // Clear the flag on error
              sourceClickInProgressRef.current = false;
            });
        }
      } catch (error) {
        console.warn('Error handling source click:', error);
        sourceClickInProgressRef.current = false;
      }
    } else {
      // Handle chat message case
      message_id = source.segment_id;
      if(message_id){
        const message = segments.find(message => message.message_id === message_id);
        if(message){
          // Find the index of the message in chatTranscriptions
          const messageIndex = segments.findIndex(msg => msg.message_id === message_id);
          if (messageIndex !== -1) {
            // Create a ref for the message element if it doesn't exist
            if (!segmentRefs.current[messageIndex]) {
              segmentRefs.current[messageIndex] = React.createRef();
            }
            
            // Scroll the message into view
            setTimeout(() => {
              const messageElement = document.querySelector(`[data-message-id="${message_id}"]`);
              if (messageElement) {
                messageElement.scrollIntoView({ behavior: "smooth", block: "nearest" });
                // Add a temporary highlight effect
                messageElement.style.backgroundColor = "rgba(111, 111, 254, 0.1)";
                setTimeout(() => {
                  messageElement.style.backgroundColor = "";
                }, 2000);
              }
            }, 100);
          }
        }
      }
    }
  }

  const toggleSegment = (item, idx, link) => {
    setSelectedSegmentIdx(idx);
    scrollToSegment(idx);
    if(!link){
      return;
    }
    if (audioRef.current) {
      audioRef.current.currentTime = item.start_time; // Seek to the start time of the segment
      audioRef.current.play(); // Play the audio
    }
  };
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  function formatEpochTime(epochMillis) {
    if (typeof epochMillis !== 'number' || epochMillis.toString().length !== 13) {
      return ""
    }
  
    const date = new Date(epochMillis);
    return date.toLocaleString(); // e.g., "5/29/2025, 2:30:45 PM"
  }
  const handleAudioTimeUpdate = (segments) => {
    if (sourceClickInProgressRef.current) return;
    const currentTime = audioRef.current.currentTime;

    // Find the segment corresponding to the current audio time
    segments.forEach((segment, idx) => {
      if (currentTime >= segment.start_time && currentTime <= segment.end_time) {
        if (segmentRefs.current[idx]) {
          // Scroll the corresponding segment into view if it's not already visible
          segmentRefs.current[idx].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });

          // Highlight the selected segment
          setSelectedSegmentIdx(idx);
        }
      }
    });
  };
  const getBorderColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"4px solid #6f6ffe"
      case 'agent':
        return"4px solid #3B82F6"
      case 'bot':
        return"4px solid gray"
      case 'system':
        return "4px solid gray"
      default:
        break;
    }
  }
  const getBackgroundColor = (actor_type) => {
    switch (actor_type) {
      case 'user':
        return"rgb(249, 250, 251)"
      case 'agent':
        return"rgb(249, 250, 251)"
      case 'bot':
        return"rgb(249, 250, 251)"
      case 'system':
        return"rgb(249, 250, 251)"
      default:
        break;
    }
  }

  const resetToDefault = () => {
    setDefaultActive(true)
    setSummaryModule(defaultConfig)
    setModuleActive(true)
  }
  const handleCloseSampleTestModal = () => {
    setShowSampleTestModal(false)
  }

  const handleGenerateSampleOutputs = async(sample_flag) => {
    try {
      setLoading(true)
      let config = {'configuration': {'conversation_summary': summaryModule}, 'active': moduleActive===true ? 1 : 0}
      let resp = await server.generateSampleModuleOutputs({
        client_id: client_id,
        service_id: Array.isArray(service_id) ? service_id[0] : service_id,
        module_name: 'conversation_summary',
        configuration: config,
        menu: "apps",
        submenu: undefined,
        email: email,
        'sample_set_flag': sample_flag
      })
      if(resp.status === 200){
        let respData = await resp.json()
        setSampleCalls(respData.data)
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  const deployModule = async(versionDescription) => {
    try {
      setLoading(true)
      let deployed = false;
      let errorMessage = 'Failed to deploy module';
      let config = {'configuration': summaryModule, 'active': moduleActive===true ? 1 : 0}
      let resp = await server.deployModule({client_id, service_id: Array.isArray(service_id) ? service_id[0] : service_id, email, menu: 'apps', submenu: undefined, module_name: 'conversation_summary', configuration: config, 'default': defaultActive, 'version_changed': current_version, version_description: versionDescription})
      if(resp.status === 200){
        showToast('Module deployed successfully', "success")
        deployed = true;
      }else{
        try {
          const errData = await resp.clone().json();
          errorMessage = errData?.message || errData?.error || errorMessage;
        } catch (_) {}
      }
      if (deployed) {
        setCloseModal()
      } else {
        showToast(errorMessage, 'error');
        setLoading(false)
      }
      setLoading(false)
    } catch (error) {
      showToast('Failed to deploy module', 'error');
      setLoading(false)
    }
  }
  const [email, setEmail] = useState(null)
    const [client_id, setClient_id] = useState(null)
    const [service_id, setService_id] = useState(null)
    const [moduleName, setModuleName] = useState(null)
  
    function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }

    const getDefaultModuleConfigs = async () => {
      try {
        setLoading(true);
        let res = await server.getDefaultModuleConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
        if(res.status === 200){
          let resp = await res.json()
          if (resp.data){
            setDefaultConfig(resp.data.configuration.conversation_summary)
          }
        }
      setLoading(false)
      } catch (error) {
        setLoading(false)
      }
    }
    const getProjectStatus = async () => {
      try {
        let res = await server.getProjectStatus(client_id, service_id, email, 'apps', undefined, window.location.href)
        if(res.status === 200){
          let resp = await res.json()
          if(resp.data && resp.data === 'completed'){
            setShowSampleTest(true);
          }
        }
      } catch (error) {
      }
    }
  
    const getModuleConfig = async() => {
      try {
        setLoading(true)
        let res = await server.getClientModuleConfig(client_id, service_id,email,'apps',undefined, moduleName);
        if(res.status === 200){
          let resp = await res.json()
          setModuleActive(resp.data.active===1 ? true : false)
          if(resp.data && resp.data.configuration && resp.data.configuration){
            setModuleSaveDraftConfiguration(resp.data.configuration)
            setSummaryModule(resp.data.configuration.conversation_summary)
            setCurrent_version(resp.data.version)
            // setDefaultConfig(resp.data.default.configuration)
          }
        }
      setLoading(false)
      } catch (error) {
        setLoading(false)
      }
    }
  
    // Load new audio when sample changes
    useEffect(() => {
          const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
          if (user && clientID) {
            setEmail(user.email);
            setClient_id(clientID);
          }
          if (serviceID && module_name) {
            setService_id(serviceID);
            setModuleName(module_name)
          }
        }, [clientID, serviceID, module_name]);
      
        useEffect(() => {
          if (client_id && service_id && moduleName && email) {
            getModuleConfig();
            getDefaultModuleConfigs()
            getProjectStatus()
          }
        }, [client_id, service_id, moduleName, email]);
        const getModuleDraftSaveConfiguration = async () => {
          try {
            let res = await server.getModuleDraftSaveConfiguration(client_id, service_id, email, 'apps', undefined, moduleName)
            if(res.status === 200){
              let resp = await res.json()
              if(resp.data){
                setModuleSaveDraftConfiguration(resp.data.configuration)
                setSummaryModule(resp.data.configuration)
              setModuleActive(resp.data.active === 1 ? true : false)
              }else{
                showToast('No saved draft.', "error")
                setConfigType('Live Version')
              }
              // resetToDefault('draft')
            }
          } catch (error) {
          }
        }
        useEffect(()=> {
          if(configType === 'Saved Draft'){
            getModuleDraftSaveConfiguration();
          }
          if(configType === 'Default'){
            resetToDefault();
          }
          if(configType === 'Live Version' && client_id && service_id && moduleName && email){
            getModuleConfig();
          }
        }, [configType])
        const handleUpdateConfig = async () => {
          try {
            let config = {'configuration': {...summaryModule}, 'active': moduleActive===true ? 1 : 0}
            let res = await server.postModuleDraftSaveConfiguration({client_id, service_id, email, module_name: 'conversation_summary', 'configuration': config, 'menu': 'apps', 'submenu': undefined})
            if(res.status === 200) {
              showToast(await getApiToastMessage(res, 'Draft saved successfully'), "success")
            } else {
              showToast(await getApiToastMessage(res, 'Failed to save draft'), "error")
            }
          } catch (error) {
            showToast('Failed to save draft', "error")
          }
        }
        useEffect(()=> {
          if(moduleSaveDraftConfiguration){
            let config = {'configuration': {...summaryModule}, 'active': moduleActive===true ? 1 : 0}
            config_data(config)
          }
        }, [moduleSaveDraftConfiguration, moduleActive, summaryModule])

  const getLoadedConfigForDiff = () => {
    if (configType === 'Default') return defaultConfig;
    return moduleSaveDraftConfiguration;
  };

  const getCurrentConfigForDiff = () => (
    configType !== 'Default' && moduleSaveDraftConfiguration?.conversation_summary
      ? { conversation_summary: summaryModule }
      : { ...(summaryModule || {}) }
  );

  // Configuration Screen
  if (!showPreview) {
    return (
      <div className="p-3">
        <div className="mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">Conversation Summary 
                <span className='inline-flex items-center px-2 py-1 rounded-full whitespace-nowrap font-normal text-xs bg-gray-200 ml-1' style={{color: "gray"}}>{configType.replace(/^Reset to /, '')}</span>
                </h1>
              <p className="text-gray-500 text-sm mt-1">Configure detailed conversation summary generation</p>
              
              {/* Configuration Import/Export Buttons */}
              {role === 'superadmin' && <div className="flex items-center gap-2 mt-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadConfig}
                    className="hidden"
                    id="config-upload"
                  />
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 hover:bg-blue-200 flex items-center text-sm"
                    onClick={() => document.getElementById('config-upload').click()}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload Config
                  </button>
                </label>
                
                <button
                  type="button"
                  className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg border border-green-300 hover:bg-green-200 flex items-center text-sm"
                  onClick={handleDownloadConfig}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download Config
                </button>
              </div>}
            </div>
            <div style={{display: "flex", alignItems: "center"}}>
            <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input class="global-input"
                    type="checkbox" 
                    checked={moduleActive} 
                    onChange={(e) => setModuleActive(moduleActive===true ? false : true)}
                    className="sr-only"
                    id="toggle-summary"
                  />
                  <label 
                    htmlFor="toggle-summary"
                    className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${moduleActive===true ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span 
                      className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${moduleActive===true ? 'translate-x-7' : 'translate-x-0'}`}
                    />
                  </label>
                </div>
                <button 
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 mr-2 flex items-center"
                  onClick={handleUpdateConfig}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save as Draft
                </button>
                <ConfigurationDiffButton
                  title="Conversation Summary Changes"
                  originalConfig={getLoadedConfigForDiff}
                  modifiedConfig={getCurrentConfigForDiff}
                />
                {showSampleTest && <button 
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                onClick={() => setShowSampleTestModal(true)}
              >
                Manage Test Set
              </button>}
                <div className="relative inline-block mx-2">
                  <button
                    className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200"
                    onClick={() => setShowConfigDropdown((prev) => !prev)} style={{width: "10rem", justifyContent: "space-between"}}
                    type="button"
                  >
                    {configType}
                    <span className="ml-2">
                      {showConfigDropdown ? (
                        // Up arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        // Down arrow
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </span>
                  </button>
                  {showConfigDropdown && (
                    <div className="absolute z-10 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg">
                      {['Live Version', 'Saved Draft', 'Default'].map(option => (
                        <div
                          key={option}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm ${configType === option ? 'text-indigo-600' : ''}`}
                          onClick={() => {
                            setConfigType(option);
                            setShowConfigDropdown(false);
                          }}
                        >
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* <ModernTimeDropdown width="w-full mx-2" placeholder="Select Configuration" value={configType} onChange={(e)=>setConfigType(e.target.value)} options={['Reset to Default', 'Reset to Saved Draft', 'New Configuration']}/> */}
              {/* <button 
                className="px-4 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300 mr-2" style={{height: "40px", borderRadius: "4px"}}
                onClick={resetToDefault}
              >
                Reset to Default
              </button> */}
              <button 
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={() => {setShowPreview(true); handleGenerateSampleOutputs(1);}}
              >
                Preview
              </button>
            </div>
          </div>
          {
            loading && (
              <div className="flex justify-center items-center" style={{ height: "75vh", flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
              </div>
            )
          }
          

          {!loading && <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-800">Conversation Summary</h2>
                <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input class="global-input"
                    type="checkbox" 
                    checked={moduleActive} 
                    onChange={(e) => setModuleActive(moduleActive===true ? false : true)}
                    className="sr-only"
                    id="toggle-summary"
                  />
                  <label 
                    htmlFor="toggle-summary"
                    className={`block h-5 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${moduleActive===true ? 'bg-indigo-600' : 'bg-gray-200'}`}
                  >
                    <span 
                      className={`block h-5 w-5 bg-white rounded-full shadow transform duration-200 ease-in-out ${moduleActive===true ? 'translate-x-7' : 'translate-x-0'}`}
                    />
                  </label>
                </div>
              </div>
                <div className="mt-4">
                  <label htmlFor="summary-prompt" className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    id="summary-prompt"
                    rows="6"
                    className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none global-input"
                    value={summaryModule?.instruction}
                    onChange={(e) => setSummaryModule({...summaryModule, instruction: e.target.value})}
                    placeholder="Provide instructions for how the AI should generate conversation summaries..."
                  ></textarea>
                </div>
              
            </div>
          </div>}
        </div>
        {
          showSampleTestModal && (
            <div>
              <div style={{position: "fixed", left: "2%", top: "6.5%", width: "96%", height: "90%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                zIndex: "999999999", padding: "0 15px"}}>
                <SampleTestModal clientID={client_id} serviceID={service_id} emailID={email} moduleName='conversation_summary' onClose={handleCloseSampleTestModal}/>
              </div>
            </div>
          )
        }
      </div>
    );
  }
  
  // Preview Screen
  return (
    <div className="p-3">
      <div className="mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Conversation Summary</h1>
            <p className="text-gray-500 text-sm mt-1">Sample results</p>
          </div>
          <div className="flex space-x-3">
            <button 
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 focus:outline-none"style={{height: "40px", borderRadius: "4px"}}
              onClick={() => {setShowPreview(false); setExpandedSample(null); setSelectedSample(0); setSampleCalls([])}}
            >
              Go Back
            </button>
            <button onClick={() => {setExpandedSample(null); setSelectedSample(0); setSampleCalls([]); setLoading(true); handleGenerateSampleOutputs(0);}} className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200 focus:outline-none"style={{height: "40px", borderRadius: "4px"}}>
              Run Different Sample
            </button>
            <ConfigurationDiffButton
              title="Conversation Summary Changes"
              originalConfig={getLoadedConfigForDiff}
              modifiedConfig={getCurrentConfigForDiff}
              triggerLabel="Deploy"
              showTriggerIcon={false}
              disabled={loading}
              triggerClassName={`px-4 py-1 h-10 ${loading ? 'bg-gray-500 hover:bg-gray-600' : 'bg-indigo-600 hover:bg-indigo-700'}  text-white rounded  focus:outline-none`}
              onNext={() => setShowDeployConfirmation(true)}
            />
          </div>
        </div>
        <DeployConfirmationModal
          isOpen={showDeployConfirmation}
          confirmationText={`Changes will be deployed in ${client_id || 'N/A'}, ${service_id || 'N/A'} and ${module_name || 'conversation_summary'}`}
          onClose={() => setShowDeployConfirmation(false)}
          onConfirm={(versionDescription) => {
            setShowDeployConfirmation(false);
            deployModule(versionDescription);
          }}
        />

        <div className="bg-white rounded-lg overflow-hidden mb-6" style={{ height: "75vh", overflow: "auto" }}>
  {!loading && sampleCalls.map((sampleCall, index) => (
    <div key={sampleCall['call_id']} className='my-2'>
      <div onClick={() => {
          setExpandedSample(expandedSample === sampleCall['call_id'] ? null : sampleCall['call_id']);
          setSelectedSample(index);
        }} className='p-4 border-b border-gray-200 bg-gray-50 cursor-pointer rounded-lg'>
      <div 
        className="flex justify-between items-center">
        <div className="flex items-center">
          <span className="font-medium text-gray-800">{sampleCall.name}</span>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-500 transform transition-transform ${expandedSample === sampleCall['call_id'] ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
      <div className="text-sm text-gray-600 mt-1 capitalize" style={{fontSize: "12px"}}>
                        ID: {sampleCall.call_id} | Time: {sampleCall.call_time} | Employee: {sampleCall.employee_name} | Record Type: {sampleCall.record_type}
                      </div>
      </div>
      
      {expandedSample === sampleCall['call_id'] && (
        <div className="p-4 border shadow rounded-lg flex">
          {/* Output Section */}
          <div className="border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.65"}}>
            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center" style={{ justifyContent: "space-between" }}>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"></path>
                </svg>
                <h3 className="font-medium text-gray-800">Output</h3>
              </div>
            </div>
            <div className="mt-2">
              <div className="rounded">
                <div className="whitespace-pre-wrap text-sm text-gray-700">
                  {sampleCall['processed_analytics'] && sampleCall['processed_analytics']['conversation_summary'] && (
                    <FeedbackSection key="conversation_summary"
                    dataKey="Conversation Summary"
                    data={sampleCall['processed_analytics']['conversation_summary']} source_click={(e)=>handleSourceClick(e, sampleCall.segments)}/>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Audio Section */}
          <div className="ml-2 border border-gray-200 rounded-lg overflow-hidden" style={{flex: "0.35"}}>
            <div className="p-2 bg-gray-50 border-b border-gray-200 flex items-center">
              <div className="flex items-center">
                {sampleCall.record_type === 'call' ? (
                  <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
                </svg>
                  ) : (<IoChatbubbleOutline style={{marginRight: "0.5rem", height: "30px"}}/>)}
                <h3 className="font-medium text-gray-800">{sampleCall.record_type === 'call' ? 'Audio' : 'Chat' }</h3>
              </div>
            </div>
            {sampleCall.record_type === 'call' && <div className="p-2 bg-white">
              <audio controls key={sampleCall.record_link} ref={audioRef} onTimeUpdate={()=>handleAudioTimeUpdate(sampleCall.segments)} style={{ width: "96%", margin: "0 1rem", height: "40px" }}>
                <source src={sampleCall.record_link} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </div>}
            <hr/>
            <div style={{fontSize: "13px", margin: "10px", overflow: 'auto', minHeight: "30vh", maxHeight: "40vh"}}>
            {
              sampleCall.record_type === 'call' && Array.isArray(sampleCall.segments) && sampleCall.segments.map((item, idx)=> {
                        const isSelected = selectedSegmentIdx === idx;
                        return (
                          <div key={`segment_${idx}`} ref={(el) => (segmentRefs.current[idx] = el)} onClick={()=>toggleSegment(item, idx, sampleCall.record_link)} style={{marginBottom: "10px", borderLeft: isSelected ? "4px solid #6f6ffe":"", background: isSelected ? "rgba(249, 250, 251, 1)":"",
                            padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                              <div style={{fontSize: "14px", color: "grey"}}>{item.speaker}</div>
                              <div style={{fontSize: "14px", color: "grey"}}>{formatTime(item.start_time)}</div>
                            </div>
                            <div style={{fontSize: "15px"}}>{item.text}</div>
                          </div>
                        )
                      })
                    }
            {
              sampleCall.record_type === 'chat' && Array.isArray(sampleCall.segments) && sampleCall.segments.map((item, idx)=> {
                return (
                  <div key={`segment_${idx}`}
                  data-message-id={item.message_id}
                  style={{marginBottom: "10px", borderLeft: getBorderColor(item.actor_type), background: getBackgroundColor(item.actor_type),
                    padding: "2px 5px", minHeight: "50px", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", cursor: "pointer"}}>
                    <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                      <div style={{fontSize: "10px", display: "flex", alignItems: "center"}}>
                        <span className='' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.actor_type}</span>
                        {item.message_type !== 'normal' && <span className=' ml-1' style={{padding: "2px 8px", borderRadius: "10px", border: "1px solid rgba(0, 0, 0, 0.26)"}}>{item.message_type}</span>}
                      </div>
                      <div style={{fontSize: "13px", color: "grey"}}>{formatEpochTime(item.timestamp)}</div>
                    </div>
                    <div style={{fontSize: "14px"}}><span style={{whiteSpace: "pre-line"}}>{item.content?.text?.content} <br/></span></div>
                    {item.content.image && item.content.image.url && 
                      <div style={{fontSize: "15px", whiteSpace: "pre-line"}}>
                        <br/>
                       Image Shared - {item.content.image.url}
                      </div>
                      }
                    {
                      item.content.buttons && item.content.buttons.length > 0 && (
                        <div>
                          <br/>
                          {item.actor_type === 'user' ? (
                            <span>Button - Selected Option - {item.content.buttons.map(button => button.text).join(", ")}</span>
                          ) : (
                            <span>Button - Options - {item.content.buttons.map(button => button.text).join(", ")}</span>
                          )}
                        </div>
                      )
                    }
                  </div>
                )
                      })
                    }
            </div>
          </div>
          
          
        </div>
      )}
    </div>
  ))}
  
  {loading && (
    <div className="flex justify-center items-center" style={{ height: "75vh", flexDirection: "column" }}>
      <div className="loader"></div>
      <div style={{ textAlign: "center" }}>Loading <br/> Please Wait</div>
    </div>
  )}
</div></div>
    </div>
  );
};

export default SummaryConfigure
